import {
  selectOutboundMonth,
  selectInboundMonth,
  selectDay,
  selectWholeYear,
  setIsReturn,
  selectCheckOutMonthForHotels,
  selectCheckInMonthForHotels,
  selectDayForHotel,
} from '../../actions/date-select-actions';
import { setInitialDirectnessFilter } from '../../actions/filter-actions';
import { setInitialSearchParams } from '../../actions/initial-search-param-actions';
import {
  setBaseRequestPath,
  setJhaToken,
} from '../../actions/initialisation-actions';
import {
  setTranslations,
  setLocalisationVars,
  setFirstDayOfWeek,
} from '../../actions/localisation-actions';
import priceTrackingActions from '../../actions/price-tracking-actions';
import setRoute from '../../actions/route-actions';
import setStay from '../../actions/stay-actions';
import { blockDateSelection } from '../../actions/ui-actions';
import { setViewId } from '../../actions/view-id-actions';
import * as constants from '../constants/dataTypeConstants';

import type { Store } from 'redux';
import type { BrowseContextProviderData } from 'src/services/contextAdaptor/types';

type FirstDayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;
const DEFAULT_FIRST_DAY_OF_WEEK: FirstDayOfWeek = 1;

const isFirstDayOfWeek = (value: number): value is FirstDayOfWeek =>
  Number.isInteger(value) &&
  (value === 0 ||
    value === 1 ||
    value === 2 ||
    value === 3 ||
    value === 4 ||
    value === 5 ||
    value === 6);

const normaliseFirstDayOfWeek = (firstDayOfWeek: number): FirstDayOfWeek =>
  isFirstDayOfWeek(firstDayOfWeek) ? firstDayOfWeek : DEFAULT_FIRST_DAY_OF_WEEK;

export type InitialiseContext = {
  dates: {
    outboundMonth: Date | null;
    inboundMonth: Date | null;
    outboundDay?: Date | null;
    inboundDay?: Date | null;
    tripType: string;
    wholeYear: boolean;
  };
  localisation: {
    translations: Record<string, string>;
    locale: string;
    market: string;
    currency: string;
    firstDayOfWeek: number;
  };
  route: {
    originId: string;
    originName: string;
    destinationId: string;
    destinationName: string;
    destinationCityName: string;
    destinationEntityId: string;
    originEntityId: string;
  };
  browse: {
    baseUrl?: string;
    providerData?: BrowseContextProviderData;
    viewId?: string;
    jhaToken?: string;
  };
  initialState: {
    preferDirects: boolean;
  };
  stay: {
    adults: number | null;
    childrenAges: number[];
  };
};

const initInitialSearchParams = (store: Store, context: InitialiseContext) => {
  const today = new Date();
  const month = context.dates.outboundMonth
    ? context.dates.outboundMonth.getMonth() + 1
    : today.getMonth() + 1;
  const year = context.dates.outboundMonth
    ? context.dates.outboundMonth.getFullYear()
    : today.getFullYear();

  store.dispatch(
    setInitialSearchParams(
      context.route.originId,
      context.route.destinationId,
      context.route.destinationName,
      month,
      year,
    ),
  );
};

const initFixedDates = (store: Store, dates: InitialiseContext['dates']) => {
  if (!dates.outboundMonth) return;

  store.dispatch(
    selectOutboundMonth(
      dates.outboundMonth.getMonth() + 1,
      dates.outboundMonth.getFullYear(),
      constants.AUTOMATED_SOURCE,
    ),
  );

  store.dispatch(
    selectCheckInMonthForHotels(
      dates.outboundMonth.getMonth() + 1,
      dates.outboundMonth.getFullYear(),
      constants.AUTOMATED_SOURCE,
    ),
  );

  // by default checkout === checkin
  store.dispatch(
    selectCheckOutMonthForHotels(
      dates.outboundMonth.getMonth() + 1,
      dates.outboundMonth.getFullYear(),
      constants.AUTOMATED_SOURCE,
    ),
  );

  if (dates.inboundMonth) {
    store.dispatch(
      selectInboundMonth(
        dates.inboundMonth.getMonth() + 1,
        dates.inboundMonth.getFullYear(),
        constants.AUTOMATED_SOURCE,
      ),
    );

    store.dispatch(
      selectCheckOutMonthForHotels(
        dates.inboundMonth.getMonth() + 1,
        dates.inboundMonth.getFullYear(),
        constants.AUTOMATED_SOURCE,
      ),
    );
  }

  if (dates.outboundDay) {
    store.dispatch(
      selectDay(
        dates.outboundDay.getDate(),
        constants.OUTBOUND_DIRECTION,
        constants.AUTOMATED_SOURCE,
      ),
    );
    store.dispatch(
      selectDayForHotel(
        dates.outboundDay.getDate(),
        constants.OUTBOUND_DIRECTION,
        constants.AUTOMATED_SOURCE,
      ),
    );
    // by default checkout === checkin
    store.dispatch(
      selectDayForHotel(
        dates.outboundDay.getDate(),
        constants.INBOUND_DIRECTION,
        constants.AUTOMATED_SOURCE,
      ),
    );
  }

  if (dates.inboundDay) {
    store.dispatch(
      selectDay(
        dates.inboundDay.getDate(),
        constants.INBOUND_DIRECTION,
        constants.AUTOMATED_SOURCE,
      ),
    );
    store.dispatch(
      selectDayForHotel(
        dates.inboundDay.getDate(),
        constants.INBOUND_DIRECTION,
        constants.AUTOMATED_SOURCE,
      ),
    );
  }
};

const initWholeyearDates = (
  store: Store,
  dates: InitialiseContext['dates'],
) => {
  store.dispatch(blockDateSelection());
  const placeholderDates: InitialiseContext['dates'] = {
    outboundMonth: new Date(),
    inboundMonth: null,
    outboundDay: null,
    inboundDay: null,
    tripType: dates.tripType,
    wholeYear: true,
  };
  if (dates.tripType === constants.RETURN_TRIP_TYPE) {
    placeholderDates.inboundMonth = new Date();
  }
  initFixedDates(store, placeholderDates);
  store.dispatch(selectWholeYear(true));
};

const initDates = (store: Store, dates: InitialiseContext['dates']) => {
  store.dispatch(setIsReturn(dates.tripType === constants.RETURN_TRIP_TYPE));

  if (dates.wholeYear) {
    initWholeyearDates(store, dates);
  } else {
    initFixedDates(store, dates);
  }
};

const initLocalisation = (
  store: Store,
  localisation: InitialiseContext['localisation'],
) => {
  store.dispatch(setTranslations(localisation.translations));
  store.dispatch(
    setLocalisationVars(
      localisation.locale,
      localisation.market,
      localisation.currency,
    ),
  );
  store.dispatch(
    setFirstDayOfWeek(normaliseFirstDayOfWeek(localisation.firstDayOfWeek)),
  );
};

const initRoute = (store: Store, route: InitialiseContext['route']) => {
  const {
    destinationCityName,
    destinationEntityId,
    destinationId,
    destinationName,
    originEntityId,
    originId,
    originName,
  } = route;
  store.dispatch(
    setRoute({
      originId,
      originName,
      destinationId,
      destinationName,
      destinationCityName,
      destinationEntityId,
      originEntityId,
    }),
  );
};

const initBrowse = (store: Store, browse: InitialiseContext['browse']) => {
  if (browse.baseUrl) store.dispatch(setBaseRequestPath(browse.baseUrl));
  if (browse.providerData) {
    store.dispatch(priceTrackingActions.setProviderData(browse.providerData));
  }
  if (browse.viewId) {
    store.dispatch(setViewId(browse.viewId));
  }
  if (browse.jhaToken) {
    store.dispatch(setJhaToken(browse.jhaToken));
  }
};

const initState = (
  store: Store,
  initialState: InitialiseContext['initialState'],
) => {
  store.dispatch(setInitialDirectnessFilter(initialState.preferDirects));
};

const initStay = (store: Store, stay: InitialiseContext['stay']) => {
  store.dispatch(setStay(stay));
};

const initialise = (store: Store, context: InitialiseContext) => {
  initInitialSearchParams(store, context);
  initDates(store, context.dates);
  initLocalisation(store, context.localisation);
  initRoute(store, context.route);
  initBrowse(store, context.browse);
  initState(store, context.initialState);
  initStay(store, context.stay);
};

export default initialise;
